import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  UnorderedList,
  OrderedList,
  ListItem,
} from '@chakra-ui/react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { monthLong } from '../../utils/intlFormatters';
import {
  movementsNamedIn, receiptsOf, recordOfLink, recordsOfList, splitForReceipts,
  textOf as nodeText,
} from './chatRecords';
import { MessageRecords, Receipts, RecordChip, RecordRows } from './chatRecordViews';

// A table cell: its text, with a chip where the assistant pointed at a record
// (MI-343). A movement is never a chip, so in a cell it stays as its text.
const cellContent = (cell) => (cell.children || []).map((child, i) => {
  const record = recordOfLink(child);
  if (record && record.type !== 'movement') {
    return <RecordChip key={i} type={record.type} id={record.id}>{nodeText(child)}</RecordChip>;
  }
  return nodeText(child);
});

// A value is "numeric" if, ignoring grouping separators, sign, and a trailing
// currency code, what remains is a number — e.g. "1,234.50", "-526.66 USD".
const isNumericText = (text) => {
  const t = (text || '').trim();
  return t !== '' && /^[-+]?[\d.,]+(\s*[A-Za-z]{3})?$/.test(t);
};

// Turn ISO dates (2025-10-13) into a friendlier localized form — month name
// instead of a number, e.g. "Octubre 13, 2025" (month-day-year per i18n rule).
export const formatFriendlyDates = (text) =>
  text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (match, y, mo, d) => {
    const name = monthLong(parseInt(mo, 10) - 1);
    return name ? `${name} ${parseInt(d, 10)}, ${y}` : match;
  });

// The agent writes math in LaTeX delimiters (\[ … \] display, \( … \) inline),
// which markdown renders as literal brackets. remark-math only understands
// dollar delimiters, so translate them. `$` alone stays literal on purpose —
// `singleDollarTextMath` is off below because money amounts ($1,234) are
// everywhere in this app and would otherwise be parsed as math.
export const normalizeMath = (text) =>
  text
    .replace(/\\\[([\s\S]+?)\\\]/g, (_, body) => `\n\n$$${body.trim()}$$\n\n`)
    .replace(/\\\(([\s\S]+?)\\\)/g, (_, body) => `$$${body.trim()}$$`);

const rowsOf = (section) =>
  (section?.children || [])
    .filter((r) => r.tagName === 'tr')
    .map((r) => (r.children || []).filter(
      (c) => c.tagName === 'th' || c.tagName === 'td'
    ));

// Split a markdown table node into headers + body rows of plain text, so both
// the table and the card renderings work off the same shape.
const readTable = (node) => {
  const sections = node.children || [];
  const headRows = rowsOf(sections.find((c) => c.tagName === 'thead'));
  const bodyRows = rowsOf(sections.find((c) => c.tagName === 'tbody'));

  const colCount = Math.max(
    0,
    ...headRows.map((r) => r.length),
    ...bodyRows.map((r) => r.length)
  );
  const numericCol = Array.from({ length: colCount }, (_, ci) => {
    const vals = bodyRows
      .map((r) => nodeText(r[ci]))
      .filter((t) => t.trim() !== '');
    return vals.length > 0 && vals.every(isNumericText);
  });

  return { headRows, bodyRows, colCount, numericCol };
};

// Render a markdown table as a Chakra table, deciding alignment per COLUMN
// (header + body together) so numeric columns are right-aligned and their
// titles line up with the values — not per cell, which misaligned headers.
const MarkdownTable = ({ node }) => {
  const { headRows, bodyRows, numericCol } = readTable(node);

  return (
    <TableContainer my={2} overflowX="auto" maxW="100%">
      <Table size="sm" variant="simple">
        {headRows.length > 0 && (
          <Thead>
            {headRows.map((cells, ri) => (
              <Tr key={ri}>
                {cells.map((cell, ci) => (
                  <Th
                    key={ci}
                    px={3}
                    py={2}
                    borderColor="redesign.border"
                    whiteSpace="nowrap"
                    isNumeric={numericCol[ci]}
                  >
                    {nodeText(cell)}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
        )}
        <Tbody>
          {bodyRows.map((cells, ri) => (
            <Tr key={ri}>
              {cells.map((cell, ci) => (
                <Td
                  key={ci}
                  px={3}
                  py={2}
                  borderColor="redesign.border"
                  whiteSpace="nowrap"
                  isNumeric={numericCol[ci]}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {cellContent(cell)}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

// The phone's table: a 14px card, in its own horizontal scroller so a wide table
// never pushes the page sideways, with a fade on each edge while more
// columns are hidden there. Hairlines in the app's border colour, since
// gray.200 glares in dark mode.
const PhoneMarkdownTable = ({ node }) => {
  const { headRows, bodyRows, numericCol } = readTable(node);
  const scrollRef = useRef(null);
  const [fade, setFade] = useState(false);
  const [fadeLeft, setFadeLeft] = useState(false);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    setFadeLeft(el.scrollLeft > 0);
  }, []);
  useLayoutEffect(updateFade, [updateFade, node]);
  useEffect(() => {
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, [updateFade]);

  const cell = {
    px: 3, py: '6px', borderColor: 'redesign.border', whiteSpace: 'nowrap', fontSize: '14px',
  };

  return (
    <Box
      position="relative"
      my={2}
      bg="redesign.bg.card"
      borderWidth="1px"
      borderColor="redesign.border"
      borderRadius="12px"
      overflow="hidden"
    >
      <Box ref={scrollRef} overflowX="auto" overscrollBehaviorX="contain" onScroll={updateFade}>
        <Table size="sm" variant="simple" sx={{ 'tbody tr:last-of-type td': { borderBottomWidth: 0 } }}>
          {headRows.length > 0 && (
            <Thead>
              {headRows.map((cells, ri) => (
                <Tr key={ri}>
                  {cells.map((c, ci) => (
                    <Th
                      key={ci}
                      {...cell}
                      py="5px"
                      fontSize="13px"
                      fontWeight="600"
                      textTransform="none"
                      letterSpacing="normal"
                      color="phone.muted"
                      isNumeric={numericCol[ci]}
                    >
                      {nodeText(c)}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
          )}
          <Tbody>
            {bodyRows.map((cells, ri) => (
              <Tr key={ri}>
                {cells.map((c, ci) => (
                  <Td
                    key={ci}
                    {...cell}
                    isNumeric={numericCol[ci]}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {cellContent(c)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {fadeLeft && (
        <Box
          position="absolute"
          top={0}
          left={0}
          bottom={0}
          w="32px"
          pointerEvents="none"
          background="linear-gradient(to left, transparent, var(--chakra-colors-redesign-bg-card))"
        />
      )}
      {fade && (
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          w="32px"
          pointerEvents="none"
          background="linear-gradient(to right, transparent, var(--chakra-colors-redesign-bg-card))"
        />
      )}
    </Box>
  );
};

// Render GFM markdown (tables, lists) plus LaTeX math from the assistant as
// styled Chakra elements — react-markdown emits bare HTML otherwise, which
// reads as a jumbled wall of pipes.
//
// The narrow panel briefly rendered tables as one card per row to avoid
// horizontal scrolling. It traded that scroll for a very tall one — twenty
// rows became ~3,000px of stacked cards — so the table (and its horizontal
// scroll) is back in both views.
//
// Record links (MI-343): the assistant points at a record with
// `[text](record:type/id)`. Inside a sentence that is a chip; a list of nothing
// but such links is a card of rows; a movement named in a sentence is a row
// under it, never a chip.
const RecordLink = ({ href, children }) => {
  const record = recordOfLink({ type: 'element', tagName: 'a', properties: { href } });
  if (!record) return <a href={href}>{children}</a>;
  if (record.type === 'movement') return <>{children}</>;
  return <RecordChip type={record.type} id={record.id}>{children}</RecordChip>;
};

// A list that only lists records is drawn as their rows; any other stays a list.
const listOf = (List) => ({ children, node }) => {
  const records = recordsOfList(node);
  if (records) return <RecordRows items={records} />;
  return <List pl={4} spacing={1} my={2}>{children}</List>;
};

const namedRows = (node) => {
  const movements = movementsNamedIn(node);
  return movements.length > 0 ? <RecordRows items={movements} /> : null;
};

const COMPONENTS = {
  table: MarkdownTable,
  a: RecordLink,
  ul: listOf(UnorderedList),
  ol: listOf(OrderedList),
  li: ({ children, node }) => <ListItem>{children}{namedRows(node)}</ListItem>,
  p: ({ children, node }) => <><Text mb={2}>{children}</Text>{namedRows(node)}</>,
};

// On the phone answers read at 16px/1.5, the size of the rest of its text.
const PHONE_COMPONENTS = {
  ...COMPONENTS,
  table: PhoneMarkdownTable,
  p: ({ children, node }) => (
    <><Text mb={2} fontSize="16px" lineHeight="1.5">{children}</Text>{namedRows(node)}</>
  ),
};

// react-markdown drops the address of any link whose scheme it does not know.
const keepRecordLinks = (url) => (url.startsWith('record:') ? url : defaultUrlTransform(url));

const REMARK_PLUGINS = [remarkGfm, [remarkMath, { singleDollarTextMath: false }]];
const REHYPE_PLUGINS = [[rehypeKatex, { throwOnError: false, strict: false }]];

// Shared by the full /chat page and the floating assistant widget so both
// render responses identically. A long formula scrolls inside its own box
// instead of widening the message bubble. `phone` sets the phone's type
// sizes and its table.
//
// `records` and `searches` are what the backend kept with the message: what a
// row shows beside its record, and what the chat created or changed. Those
// receipts are drawn here, from that, whatever the text says. `position` is the
// message's place in its conversation: a receipt looks back from there for the
// search that found what it changed.
export default function ChatMarkdown({ content, phone = false, records, searches, position }) {
  const receipts = receiptsOf(records, content);
  const [before, after] = receipts.length > 0 ? splitForReceipts(content) : [content || '', ''];
  const markdown = (text) => (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={phone ? PHONE_COMPONENTS : COMPONENTS}
      urlTransform={keepRecordLinks}
    >
      {formatFriendlyDates(normalizeMath(text))}
    </ReactMarkdown>
  );
  return (
    <Box
      sx={{ '& .katex-display': { overflowX: 'auto', overflowY: 'hidden', py: 1 } }}
      fontSize={phone ? '16px' : undefined}
      lineHeight={phone ? '1.5' : undefined}
    >
      <MessageRecords records={records} searches={searches} position={position}>
        {markdown(before)}
        <Receipts records={receipts} />
        {after && markdown(after)}
      </MessageRecords>
    </Box>
  );
}
